import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import { household, showToast, friendlyError, bumpListsVersion } from '../store.js';
import { __ } from '../i18n.js';
import Modal from './Modal.jsx';

/**
 * Create a lijstje or vaste lijst, or edit an existing one. Mirrors
 * CategoryEditModal's exact shape: isCreate detection from a missing id,
 * the dismissedRef guard against a save that resolves after the user has
 * already backdrop-dismissed the modal (api.js's request() has no
 * AbortController), and onSaved()/onClose() kept distinct for the same
 * reason.
 *
 * Scope (personal/shared) is still create-only, per
 * ETM_Lists::create_list()'s docblock. is_template is NOT: an accidental
 * tick on create used to be unfixable, so the edit path sends it too. It
 * sits BELOW the shared checkbox and carries a hint line, because the two
 * identical checkboxes read as two settings of equal weight while one of
 * them actually decides what kind of thing you are making.
 *
 * `templates` is used on the create path only: the startvulling picker
 * pours the ticked vaste lijsten into the new list server-side, in the
 * same request. Hidden when the new list is itself a vaste lijst.
 *
 * group_by_category is offered on BOTH paths, and on vaste lijsten too:
 * pour_into() carries each item's category across, so a grouped template
 * seeds a grouped shop. It is the only way to turn the per-item tag
 * affordance on — an untoggled list is byte-for-byte the pre-feature
 * screen.
 */
export default function ListEditModal({ editingList, templates = [], onClose, onSaved }) {
  const isCreate = !editingList.id;
  const [name, setName] = useState(editingList.name || '');
  const [isTemplate, setIsTemplate] = useState(!!editingList.is_template);
  const [isShared, setIsShared] = useState(!!household.value);
  const [groupByCategory, setGroupByCategory] = useState(!!editingList.group_by_category);
  const [pourIds, setPourIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const dismissedRef = useRef(false);
  useEffect(() => () => {
    dismissedRef.current = true;
  }, []);

  function togglePour(id) {
    setPourIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showToast(__('Geef het lijstje een naam', 'etm'), 'error');
      return;
    }
    setSaving(true);
    try {
      let message = isCreate ? __('Lijstje toegevoegd', 'etm') : __('Lijstje opgeslagen', 'etm');
      if (isCreate) {
        const res = await api.lists.create({
          name: trimmedName,
          is_template: isTemplate,
          is_shared: isShared,
          group_by_category: groupByCategory,
          template_ids: isTemplate ? [] : pourIds,
        });
        if (res && res.added > 0) {
          message = __('Lijstje toegevoegd met %d items.', 'etm').replace('%d', res.added);
        }
      } else {
        await api.lists.update(editingList.id, {
          name: trimmedName,
          is_template: isTemplate,
          group_by_category: groupByCategory,
        });
      }
      bumpListsVersion();
      showToast(message, 'success');
      if (!dismissedRef.current) {
        onSaved({ name: trimmedName, is_template: isTemplate, group_by_category: groupByCategory });
      }
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isCreate ? __('Nieuw lijstje', 'etm') : __('Lijstje bewerken', 'etm')} onClose={onClose}>
      <div class="etm-form-group">
        <label class="etm-form-label" for="etm-list-name">
          {__('Naam', 'etm')}
        </label>
        <input
          id="etm-list-name"
          class="etm-form-input"
          type="text"
          value={name}
          onInput={(e) => setName(e.currentTarget.value)}
          placeholder={__('Bijv. Weekboodschappen', 'etm')}
          maxLength={140}
          autofocus
        />
      </div>

      {isCreate && household.value ? (
        <div class="etm-form-group etm-checkbox-group">
          <input
            id="etm-list-shared"
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.currentTarget.checked)}
          />
          <label for="etm-list-shared">{__('Gedeeld met huishouden', 'etm')}</label>
        </div>
      ) : null}

      <div class="etm-checkbox-group">
        <input
          id="etm-list-template"
          type="checkbox"
          checked={isTemplate}
          onChange={(e) => setIsTemplate(e.currentTarget.checked)}
        />
        <label for="etm-list-template">{__('Vaste lijst', 'etm')}</label>
      </div>
      <p class="etm-field-hint">
        {__('Een vaste lijst vink je niet af. Je voegt hem later in één tap toe aan een gewoon lijstje.', 'etm')}
      </p>

      <div class="etm-checkbox-group">
        <input
          id="etm-list-group-by-category"
          type="checkbox"
          checked={groupByCategory}
          onChange={(e) => setGroupByCategory(e.currentTarget.checked)}
        />
        <label for="etm-list-group-by-category">{__('Groepeer in supermarkt-categorieën', 'etm')}</label>
      </div>
      <p class="etm-field-hint">
        {__('Items krijgen een optioneel categorielabel; in winkelmodus kun je op categorie sorteren.', 'etm')}
      </p>

      {isCreate && !isTemplate && templates.length > 0 ? (
        <>
          <span class="etm-form-label">{__('Beginnen met', 'etm')}</span>
          {templates.map((t) => (
            <div class="etm-form-group etm-checkbox-group" key={t.id}>
              <input
                id={`etm-list-pour-${t.id}`}
                type="checkbox"
                checked={pourIds.includes(t.id)}
                onChange={() => togglePour(t.id)}
              />
              <label for={`etm-list-pour-${t.id}`}>{t.name}</label>
            </div>
          ))}
        </>
      ) : null}

      <button type="button" class="etm-btn etm-btn-primary" disabled={saving} onClick={handleSave}>
        {__('Opslaan', 'etm')}
      </button>
    </Modal>
  );
}
